import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { TimePicker } from '@atlaskit/datetime-picker';
import { type SelectProps } from '@atlaskit/select';

import { selectComponents } from '../../constants';
import { getDayValue, getDefaultTimes, getTimeValue } from '../../utils';
import { type DayOption } from '../../utils/types';
import { DayPicker } from '../day-picker';
import { FullWidthFlex } from '../styled';

import messages from './messages';
import { InputWrapper, Label } from './styled';
import { type DayTimePickerProps } from './types';

export const DayTimePicker = ({
	autoFocus,
	label,
	value,
	onChange,
	weekStartDay,
	defaultValue,
	testId,
	isDisabled,
	timeIsEditable,
	timeSlotIndex,
	variant = 'default',
}: DayTimePickerProps) => {
	const { formatMessage } = useIntl();
	const timePickerRef = useRef<typeof TimePicker>(null);
	const [day, setDay] = useState<DayOption | undefined>(getDayValue(value, variant));
	const [time, setTime] = useState<string | undefined>(getTimeValue(value));

	const handleChangeFromDay = (selectedOption: DayOption) => {
		setDay(selectedOption);

		const isAllValuesSet = selectedOption && time !== undefined && time !== null;

		if (onChange && isAllValuesSet) {
			const [hour, minute] = time.split(':');

			onChange({
				day: selectedOption.value,
				hour: parseInt(hour),
				minute: parseInt(minute),
			});
		}
	};

	const handleChangeFromTime = (selectedOption: string) => {
		setTime(selectedOption);

		const isAllValuesSet = day && selectedOption;

		if (onChange && isAllValuesSet) {
			const [hour, minute] = selectedOption.split(':');

			onChange({
				day: day.value,
				hour: parseInt(hour),
				minute: parseInt(minute),
			});
		}
	};

	useEffect(() => {
		if (value) {
			setDay(getDayValue(value, variant));
			setTime(getTimeValue(value));
		}
	}, [value, variant]);

	return (
		<FullWidthFlex rowGap={0.5} flexDirection="column">
			{timeSlotIndex < 1 && label && <Label>{label}</Label>}
			<FullWidthFlex flexShrink={0} columnGap={0.5}>
				<InputWrapper>
					<DayPicker
						autoFocus={autoFocus}
						weekStartDay={weekStartDay}
						value={day}
						defaultValue={getDayValue(defaultValue, variant)}
						onChange={handleChangeFromDay}
						testId={testId && `${testId}-day-picker`}
						isDisabled={isDisabled}
						components={selectComponents[variant]}
						spacing={variant}
						variant={variant}
						aria-label={
							label &&
							formatMessage(messages.dayLabel, {
								label,
								index: timeSlotIndex + 1,
							})
						}
					/>
				</InputWrapper>
				<InputWrapper>
					<TimePicker
						ref={timePickerRef}
						times={getDefaultTimes()}
						// @ts-ignore -  Property 'isClearable' does not exist on type 'TimePickerProps'
						isClearable={false}
						defaultValue={getTimeValue(defaultValue)}
						onChange={handleChangeFromTime}
						isDisabled={isDisabled}
						timeIsEditable={timeIsEditable}
						hideIcon={variant === 'compact'}
						// @ts-ignore - https://product-fabric.atlassian.net/browse/DSP-21000
						selectProps={
							{
								isClearable: false,
								spacing: variant,
							} as Omit<SelectProps<DayOption, false>, 'components'>
						}
						testId={testId && `${testId}-time-picker`}
						// time picker doesn't accept defaultValue if value exist.
						// even if value === undefined.
						// so this is workaround to make defaultValue work.
						{...(time && { value: time })}
						label={
							label &&
							formatMessage(messages.timeLabel, {
								label,
								index: timeSlotIndex + 1,
							})
						}
					/>
				</InputWrapper>
			</FullWidthFlex>
		</FullWidthFlex>
	);
};
